<template>
    <div v-if="editor && toolbar === 'fixed'" class="flex items-center gap-1 flex-wrap rounded-lg bg-white/10 backdrop-blur px-2 py-1 mb-1">
        <button @click="editor.chain().focus().toggleBold().run()"
            class="w-7 h-7 rounded font-bold text-sm transition-colors"
            :class="editor.isActive('bold') ? 'bg-white text-slate-900' : 'text-white hover:bg-white/20'"
            :title="$t('presentation.tbBold')">
            B
        </button>
        <button @click="editor.chain().focus().toggleItalic().run()"
            class="w-7 h-7 rounded italic text-sm transition-colors"
            :class="editor.isActive('italic') ? 'bg-white text-slate-900' : 'text-white hover:bg-white/20'"
            :title="$t('presentation.tbItalic')">
            I
        </button>
        <button @click="editor.chain().focus().toggleStrike().run()"
            class="w-7 h-7 rounded line-through text-sm transition-colors"
            :class="editor.isActive('strike') ? 'bg-white text-slate-900' : 'text-white hover:bg-white/20'"
            :title="$t('presentation.tbStrike')">
            S
        </button>
        <span class="w-px h-5 bg-white/20 mx-1"></span>
        <button
            v-for="c in swatches"
            :key="c"
            @click="editor.chain().focus().setColor(c).run()"
            class="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
            :class="editor.isActive('textStyle', { color: c }) ? 'border-white' : 'border-white/30'"
            :style="{ backgroundColor: c }"
            :title="c"
        ></button>
        <label
            class="w-6 h-6 rounded-full cursor-pointer border-2 border-dashed border-white/50 hover:border-white transition-colors overflow-hidden"
            :title="$t('presentation.tbCustomColor')"
        >
            <span class="block w-full h-full" style="background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red)"></span>
            <input
                type="color"
                class="sr-only"
                :value="editor.getAttributes('textStyle').color || '#ffffff'"
                @input="editor.chain().focus().setColor($event.target.value).run()"
            />
        </label>
        <button @click="editor.chain().focus().unsetColor().run()"
            class="w-7 h-7 rounded text-white hover:bg-white/20 transition-colors"
            :title="$t('presentation.tbClearColor')">
            <span class="relative inline-block font-bold text-sm leading-none">A<XMarkIcon class="absolute -top-1.5 -right-2.5 w-3 h-3" /></span>
        </button>
    </div>
    <div v-if="editor && toolbar === 'bubble'">
        <bubble-menu :editor="editor">
            <div class="bubble-menu">
                <button @click="editor.chain().focus().toggleBold().run()"
                class="bold"
                :class="{ 'is-active': editor.isActive('bold') }">
                B
                </button>
                <button
                @click="editor.chain().focus().toggleItalic().run()"
                class="italic"
                :class="{ 'is-active': editor.isActive('italic') }">
                I
                </button>
                <button
                @click="editor.chain().focus().toggleStrike().run()"
                class="strike"
                :class="{ 'is-active': editor.isActive('strike') }">
                S
                </button>
            </div>
        </bubble-menu>
    </div>
    <editor-content :editor="editor" class="w-full h-full" :class="props.class" >

    </editor-content>
</template>

<script setup>
import { defineProps, defineEmits, defineExpose, onBeforeUnmount, ref, watch } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { BubbleMenu } from '@tiptap/vue-3/menus';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { TextStyle, Color, FontFamily, FontSize } from '@tiptap/extension-text-style';

const props = defineProps({
    content: {
        type: String,
        default: "",
    },
    class: {
        type: String,
        default: "",
    },
    spellcheck: {
        type: Boolean,
        default: false,
    },
    editable: {
        type: Boolean,
        default: true,
    },
    toolbar: {
        type: String,
        default: 'bubble', // 'bubble' | 'fixed' | 'none'
    },
});
const emit = defineEmits(['update:content', 'focus', 'blur'])

const isFocused = ref(false);

const swatches = ['#FFFFFF', '#FDE68A', '#F87171', '#34D399', '#60A5FA'];

const editor = useEditor({
    content: props.content,
    extensions: [StarterKit, TextStyle, Color, FontFamily, FontSize, BubbleMenu],
    editorProps: {
        attributes: {
            spellcheck: props.spellcheck ? 'true' : 'false',
        },
    },
    editable: props.editable,
    onUpdate: ({ editor }) => {
        emit('update:content', editor.getHTML())
    },
    onFocus: ({ editor }) => {
        isFocused.value = true;
        emit('focus', editor);
    },
    onBlur: ({ editor }) => {
        isFocused.value = false;
        emit('blur', editor);
    },
})

defineExpose({ editor, isFocused });

// Watch for external content changes and update editor
watch(() => props.content, (newContent) => {
    if (!editor.value) return;

    // Only update if the content is actually different to avoid infinite loops
    const currentContent = editor.value.getHTML();
    if (currentContent !== newContent) {
        editor.value.commands.setContent(newContent, false);
    }
})

watch(() => props.editable, (newEditable) => {
    if (!editor.value) return;
    editor.value.setOptions({ editable: newEditable });
}, { immediate: true });

onBeforeUnmount(() => {
    if (!editor.value) return;
    editor.value.destroy()
})
</script>
